import React from "react"
import BrandLogo, { BRAND } from "../shared/BrandLogo"
import { HEX, liftForDark } from "../lib/color"

/* ============================================================
   Cangkang halaman masuk: tata letak, panel visual, kepala, dan kartu —
   segalanya KECUALI isi formulirnya (anak komponen ini).

   Dipakai dua tempat dan harus tetap begitu: halaman masuk sungguhan
   (LoginScreen) dan pratinjau langsung di Pengaturan. Pratinjau yang menyalin
   tampilannya akan menyimpang dari aslinya pada perubahan pertama; yang
   memakai komponen yang sama tidak bisa.

   `branding` dikirim sebagai prop, bukan dibaca dari konteks, supaya
   pratinjau bisa menampilkan isian yang BELUM disimpan.
   ============================================================ */

// Permukaan gelap tempat teks dan aksen halaman masuk digambar (ujung gradien
// panel visual). Warna aksen dinaikkan terangnya terhadap warna ini.
const VISUAL_SURFACE = "#0D2226"

// Judul besar kustom: kata terakhir disorot dengan gaya yang sama seperti
// "Sertifikasi." pada judul bawaan. Ukurannya turun bila teksnya panjang, supaya
// 80 karakter tidak meluap dari panel.
function headlineParts(text) {
  const words = text.trim().split(/\s+/)
  const last = words.pop()
  return { lead: words.join(" "), last }
}
const headlineSize = (len) => (len <= 24 ? 64 : len <= 44 ? 52 : 40)

export default function LoginShell({ branding, lang, setLang, isMobile, minHeight = "100vh", children }) {
  // FR-28: the sign-in screen wears the institution's identity, not the
  // platform's. Falls back to neutral copy while loading or when a field is
  // left blank by that institution.
  const orgName = branding?.institution_name || "Gladhy"
  const orgTagline = branding?.tagline || ""
  const orgDesc = branding?.description || ""
  // Backdrop supplied by the institution. Blank is the common case and must
  // look deliberate, not broken — see the gradient in coverStyle below. It
  // used to fall back to a maritime photo belonging to one institution.
  const orgCover = branding?.cover_image_url || ""
  // Accent for the gradient and the highlighted word. Only a plain 6-digit hex
  // is accepted, because the gradient appends an alpha pair to it — `red` or
  // `rgb(...)` would silently produce an invalid colour and a blank panel.
  const accent = HEX.test(branding?.primary_color || "") ? branding.primary_color : BRAND.orange
  // Aksen sebagai TEKS atau titik di atas panel gelap: warna merek yang gelap
  // (biru laut, ungu tua) hilang di sana, jadi dinaikkan terangnya. Gradien
  // latar tetap memakai warna aslinya.
  const accentOnDark = liftForDark(accent, VISUAL_SURFACE)

  // Tata letak, judul, dan white-label datang dari server yang sudah
  // menyaringnya menurut paket (routes/settings.js): nilai di atas paket tiba
  // kosong, jadi layar tidak perlu tahu paket apa pun.
  const layout = ["FULL", "PLAIN"].includes(branding?.login_layout) ? branding.login_layout : "SPLIT"
  const whiteLabel = branding?.hide_platform_branding === true
  const customHeadline = (branding?.login_headline || "").trim()
  // Subjudul di bawah nama: milik lembaga; bila kosong, teks platform — kecuali
  // white-label, yang memilih tidak ada.
  const orgSubText = branding?.footer_text
    || (whiteLabel ? "" : (lang === "id" ? "Platform Pelatihan & Sertifikasi" : "Training & Certification Platform"))

  // Latar visual lembaga: gambar (bila ada) dan scrim. Dipakai panel kiri pada
  // tata letak SPLIT dan seluruh layar pada FULL.
  const coverStyle = orgCover
    ? { backgroundImage: `url('${orgCover}')`, backgroundSize: "cover", backgroundPosition: "center" }
    : {}
  // Over a photo this is a scrim that keeps the text legible; with no photo it
  // IS the artwork. Both cases have to look composed, because most institutions
  // will never upload an image.
  const scrim = (
    <div style={{
      position: "absolute", inset: 0,
      background: orgCover
        ? "linear-gradient(155deg, rgba(0,61,64,0.72) 0%, rgba(5,27,31,0.80) 60%, rgba(2,10,14,0.88) 100%)"
        : `radial-gradient(120% 90% at 12% 8%, ${accent}38 0%, transparent 58%),
           radial-gradient(90% 80% at 88% 96%, ${accent}24 0%, transparent 60%),
           linear-gradient(155deg, #0D2226 0%, #081A1E 55%, #040F13 100%)`,
    }} />
  )

  // Judul besar kustom: kata terakhir disorot seperti "Sertifikasi." pada judul bawaan.
  const headlineText = (text, highlight) => {
    const { lead, last } = headlineParts(text)
    return (
      <>
        {lead && <>{lead}<br /></>}
        <span style={{ color: highlight }}>{last}</span>
      </>
    )
  }

  const brandRow = (onDark) => (
    <div className="row gap-3" style={{ position: "relative", zIndex: 2 }}>
      <BrandLogo size={52} branding={branding} />
      <div>
        <div style={{ fontWeight: 600, fontSize: 16, color: onDark ? "white" : "var(--text)" }}>{orgName}</div>
        {orgSubText && (
          <div style={{ fontSize: 12, color: onDark ? "rgba(255,255,255,0.55)" : "var(--text-3)" }}>{orgSubText}</div>
        )}
      </div>
    </div>
  )

  // Kepala di atas formulir bila tidak ada panel visual di sampingnya: tata
  // letak FULL dan PLAIN, dan layar sempit (panel kiri disembunyikan di sana,
  // sehingga identitas lembaga tadinya hilang sama sekali).
  const onDarkHeader = layout === "FULL"
  const header = (layout !== "SPLIT" || isMobile) && (
    <div style={{ marginBottom: 24 }}>
      {brandRow(onDarkHeader)}
      {customHeadline && (
        <h1 style={{
          fontFamily: "var(--font-display, sans-serif)", fontWeight: 700,
          fontSize: 32, lineHeight: 1.1, letterSpacing: "-0.02em", marginTop: 20,
          color: onDarkHeader ? "white" : "var(--text)",
        }}>
          {headlineText(customHeadline, onDarkHeader ? accentOnDark : "var(--program-color-text)")}
        </h1>
      )}
    </div>
  )

  return (
    <div style={{
      minHeight,
      background: "#0B1417",
      display: "grid",
      gridTemplateColumns: layout === "SPLIT" && !isMobile ? "1.05fr 1fr" : "1fr",
      ...(layout === "FULL" ? { position: "relative", ...coverStyle } : {}),
    }}>
      {layout === "FULL" && scrim}
      {/* ════════════════════════════════════
          LEFT — the institution's visual (hidden on mobile)
          ════════════════════════════════════ */}
      {layout === "SPLIT" && <div style={{
        display: isMobile ? "none" : "flex",
        flexDirection: "column",
        position: "relative",
        padding: "36px 48px",
        overflow: "hidden",
        ...coverStyle,
      }}>
        {scrim}

        {/* Brand mark */}
        {brandRow(true)}

        {/* Tagline */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", zIndex: 2 }}>
          <div style={{
            display: "inline-flex", alignSelf: "flex-start",
            padding: "4px 10px",
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
            borderRadius: 999, fontSize: 11,
            textTransform: "uppercase", letterSpacing: "0.1em",
            color: "rgba(255,255,255,0.7)",
            gap: 8, alignItems: "center",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: accentOnDark, boxShadow: `0 0 8px ${accentOnDark}`, flexShrink: 0 }} />
            {orgTagline || (lang === "id" ? "Pelatihan Bersertifikat" : "Certified Training")}
          </div>

          {/* Describes the product's own cycle, so it fits a shipping company,
              a factory and a hospital alike. It used to read "Train the
              response." — oil-spill response, which is one institution's
              subject matter, not what the platform does. */}
          <h1 style={{
            fontFamily: "var(--font-display, sans-serif)",
            fontSize: customHeadline ? headlineSize(customHeadline.length) : 64, fontWeight: 700, lineHeight: 1.05,
            marginTop: 24, letterSpacing: "-0.03em", maxWidth: 540,
            color: "white",
          }}>
            {customHeadline
              ? headlineText(customHeadline, accentOnDark)
              : <>
                  {lang === "id" ? <>Latih. Uji.<br /></> : <>Train. Assess.<br /></>}
                  <span style={{ color: accentOnDark }}>
                    {lang === "id" ? "Sertifikasi." : "Certify."}
                  </span>
                </>}
          </h1>

          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.7)", maxWidth: 460, marginTop: 22, lineHeight: 1.6 }}>
            {orgDesc || (lang === "id"
              ? `${orgName} menyelenggarakan pelatihan bersertifikat dengan materi terstruktur, kehadiran terverifikasi, dan sertifikat yang dapat diperiksa keasliannya.`
              : `${orgName} runs certified training with structured material, verified attendance, and certificates anyone can authenticate.`)}
          </p>
        </div>

        {/* Footer */}
        <div className="row between" style={{ position: "relative", zIndex: 2, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
          <span>© {new Date().getFullYear()} {orgName}</span>
          <div className="row gap-4">
            <span style={{ cursor: "pointer" }}>Privacy</span>
            <span style={{ cursor: "pointer" }}>Terms</span>
            <span style={{ cursor: "pointer" }}>Help</span>
            <button
              onClick={() => setLang(lang === "id" ? "en" : "id")}
              style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "var(--font-mono)" }}
            >
              {lang === "id" ? "ID / EN" : "EN / ID"}
            </button>
          </div>
        </div>
      </div>}

      {/* ════════════════════════════════════
          RIGHT — login form
          ════════════════════════════════════ */}
      <div style={{
        background: layout === "FULL" ? "transparent" : "var(--bg)",
        color: "var(--text)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: isMobile ? 20 : 40, position: "relative", overflowY: "auto",
        minHeight,
      }}>
        {/* Faint grid background — tidak di atas gambar latar penuh */}
        {layout !== "FULL" && <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, opacity: 0.4, pointerEvents: "none" }}>
          <defs>
            <pattern id="login-grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M32 0H0V32" fill="none" stroke="var(--border-subtle)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#login-grid)" />
        </svg>}

        <div style={{ width: "100%", maxWidth: layout === "FULL" ? 440 : 380, position: "relative" }}>
          {header}
          <div style={layout === "FULL" ? {
            background: "var(--bg)", color: "var(--text)",
            borderRadius: 16, padding: isMobile ? 24 : 36,
            boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          } : undefined}>
      {children}
          </div>
        </div>
      </div>
    </div>
  )
}
